import React, { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { formatPrice } from '../../../functions/price-formater.js';
import { CAPITAL_PERIODS, buildCapitalHistory } from '../../../functions/capitalHistory.js';
import { niceTicks } from '../chartMath.js';

// Couleurs en dur (et non `var(--up)`/`var(--down)`) : ce composant est aussi utilisé sur la
// page plein écran, qui ne définit pas les variables de thème de l'accueil.
const UP = '#4ADE9E';
const UP_SOFT = 'rgba(74,222,158,0.12)';
const DOWN = '#FF7A78';
const DOWN_SOFT = 'rgba(255,122,120,0.12)';

// Nombre de paliers visés sur l'axe des valeurs en plein écran
const Y_AXIS_TICK_COUNT = 8;

const ExpandIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M9 3H4v5M15 3h5v5M9 21H4v-5M15 21h5v-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Aire simple (même tracé que l'ancien graphique de l'accueil) : taille fixe en usage normal,
// mesurée via ResizeObserver pour remplir l'écran en plein écran (+ graduations Y en plein écran).
function AreaChart({ data, up = true, gradId = 'area-g', large = false }) {
    const wrapRef = useRef(null);
    const [measured, setMeasured] = useState({ w: 340, h: 80 });

    useEffect(() => {
        if (!large || !wrapRef.current) return;
        const el = wrapRef.current;
        const update = () => {
            const rect = el.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) setMeasured({ w: rect.width, h: rect.height });
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [large]);

    const w = large ? measured.w : 340;
    const height = large ? measured.h : 80;
    const PAD = large ? { top: 10, right: 62, bottom: 10, left: 10 } : { top: 4, right: 4, bottom: 4, left: 4 };
    const iw = w - PAD.left - PAD.right;
    const ih = height - PAD.top - PAD.bottom;

    const min = Math.min(...data);
    const max = Math.max(...data);
    const dataRange = max - min;
    // Si la donnée est plate (aucune variation), on garde une marge symétrique autour de la
    // valeur unique pour que la ligne s'affiche au milieu du graphe, pas collée en bas.
    const vPad = dataRange > 0 ? dataRange * 0.08 : (Math.abs(max) || 1) * 0.05;
    const domainMin = min - vPad;
    const domainMax = max + vPad;
    const domainSpan = domainMax - domainMin;

    const stepX = iw / (data.length - 1);
    const scaleY = (v) => PAD.top + ih - ((v - domainMin) / domainSpan) * ih;
    const pts = data.map((v, i) => [PAD.left + i * stepX, scaleY(v)]);
    const path = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
    const area = `${path} L${PAD.left + iw},${PAD.top + ih} L${PAD.left},${PAD.top + ih} Z`;
    const c = up ? UP : DOWN;
    const last = pts[pts.length - 1];

    // Paliers de valeurs (axe Y, plein écran) : pas arrondi + décimales déduites du pas
    const { ticks: yTicks, step: yStep } = large
        ? niceTicks(domainMin, domainMax, Y_AXIS_TICK_COUNT)
        : { ticks: [], step: 1 };
    const yDecimals = Math.max(0, Math.ceil(-Math.log10(yStep || 1)));
    const yMidTarget = (min + max) / 2;
    const yMidTick = yTicks.length
        ? yTicks.reduce((best, v) => Math.abs(v - yMidTarget) < Math.abs(best - yMidTarget) ? v : best, yTicks[0])
        : null;

    return (
        <div ref={wrapRef} style={large ? { position: 'relative', flex: 1, minHeight: 0 } : { position: 'relative' }}>
            <svg width="100%" height={large ? '100%' : undefined} viewBox={`0 0 ${w} ${height}`} style={{ display: 'block' }}>
                <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={c} stopOpacity="0.22"/>
                        <stop offset="100%" stopColor={c} stopOpacity="0"/>
                    </linearGradient>
                </defs>

                {/* Grille horizontale (plein écran uniquement) */}
                {large && yTicks.map((val) => {
                    const y = scaleY(val);
                    if (y < PAD.top - 0.5 || y > PAD.top + ih + 0.5) return null;
                    return (
                        <line key={val}
                            x1={PAD.left} x2={w - PAD.right}
                            y1={y} y2={y}
                            stroke="rgba(255,255,255,0.06)" strokeWidth="1"
                        />
                    );
                })}

                <path d={area} fill={`url(#${gradId})`}/>
                <path d={path} fill="none" stroke={c} strokeWidth={large ? 2.2 : 1.6} strokeLinejoin="round" strokeLinecap="round"/>
                <circle cx={last[0]} cy={last[1]} r={large ? 4.5 : 3} fill={c}/>
                <circle cx={last[0]} cy={last[1]} r={large ? 8 : 5.5} fill={c} opacity="0.2"/>
            </svg>

            {/* Labels axe Y (plein écran) — paliers arrondis, palier central mis en évidence */}
            {large && yTicks.map((val) => {
                const y = scaleY(val);
                if (y < PAD.top - 0.5 || y > PAD.top + ih + 0.5) return null;
                const isMid = val === yMidTick;
                return (
                    <div key={val} style={{
                        position: 'absolute', right: 6, top: y, transform: 'translateY(-50%)',
                        fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap', pointerEvents: 'none',
                        color: isMid ? '#0A1226' : '#97A4BE',
                        background: isMid ? '#F2F5FB' : 'transparent',
                        border: isMid ? '1.5px solid #FFFFFF' : 'none',
                        borderRadius: isMid ? 6 : 0,
                        padding: isMid ? '3px 7px' : 0,
                    }}>
                        {val.toLocaleString('fr-FR', { minimumFractionDigits: yDecimals, maximumFractionDigits: yDecimals })}
                    </div>
                );
            })}
        </div>
    );
}

// Bloc "Capital total" (montant + variation + graphique + sélecteur de période), utilisé
// sur l'accueil (taille normale, avec bouton plein écran) et sur sa page plein écran (`large`).
const CapitalChart = ({ portefeuilledata, allTxs, dateJeu, large = false, fullscreenHref, gradId = 'acc-area-g' }) => {
    const [period, setPeriod] = useState('1A');
    const { periodHistory, periodPct, periodChange } = useMemo(
        () => buildCapitalHistory(portefeuilledata, allTxs, period),
        [portefeuilledata, allTxs, period]
    );
    const isUp = periodPct >= 0;

    return (
        <div style={large ? { display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' } : { position: 'relative' }}>
            <div style={{
                position: 'absolute', top: -60, right: -60, width: 200, height: 200,
                background: isUp
                    ? 'radial-gradient(closest-side, rgba(74,222,158,0.18), transparent)'
                    : 'radial-gradient(closest-side, rgba(255,122,120,0.18), transparent)',
                pointerEvents: 'none',
            }}/>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                <span style={{ color: 'var(--text-mute)', fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Capital total
                </span>
                {!large && (
                    <Link to="/PorteFeuille" style={{
                        color: 'var(--text-dim)', fontSize: 11, fontWeight: 500,
                        display: 'flex', alignItems: 'center', gap: 2, textDecoration: 'none',
                    }}>
                        Détails
                        <svg width="10" height="10" viewBox="0 0 10 10">
                            <path d="M3.5 2.5L6 5 3.5 7.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </Link>
                )}
            </div>

            <div id={!large ? 'tuto-capital-total' : undefined} style={{ marginTop: 8, position: 'relative' }}>
                <span className="num" style={{ fontSize: large ? 48 : 34, fontWeight: 700, letterSpacing: '-0.02em' }}>
                    {portefeuilledata?.montant_actuel !== undefined
                        ? formatPrice(portefeuilledata.montant_actuel)
                        : '—'}
                </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, position: 'relative', flexWrap: 'wrap' }}>
                {portefeuilledata?.montant_actuel !== undefined && (
                    <>
                        <div className="num" style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            color: isUp ? UP : DOWN,
                            background: isUp ? UP_SOFT : DOWN_SOFT,
                            padding: '3px 8px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                        }}>
                            <svg width="10" height="10" viewBox="0 0 10 10">
                                <path
                                    d={isUp ? "M2 6.5L5 3.5 8 6.5" : "M2 3.5L5 6.5 8 3.5"}
                                    stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"
                                />
                            </svg>
                            {isUp ? '+' : ''}{formatPrice(periodChange)}
                        </div>
                        <span className="num" style={{ color: isUp ? UP : DOWN, fontSize: 12, fontWeight: 600 }}>
                            {isUp ? '+' : ''}{periodPct.toFixed(2)}%
                        </span>
                    </>
                )}
                {dateJeu?.jour_x && (
                    <span style={{ color: 'var(--text-mute)', fontSize: 11 }}>
                        · {dateJeu.jour_x}j
                    </span>
                )}
            </div>

            {periodHistory.length >= 2 && (
                <div
                    id={!large ? 'tuto-capital-graph' : undefined}
                    style={{
                        marginTop: 8,
                        ...(large ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : { marginLeft: -4, marginRight: -4 }),
                    }}
                >
                    <AreaChart data={periodHistory} up={isUp} gradId={gradId} large={large} />
                </div>
            )}

            <div id={!large ? 'tuto-capital-period-filters' : undefined} style={{ display: 'flex', gap: large ? 8 : 6, justifyContent: 'space-between', marginTop: 8, paddingBottom: large ? 0 : 10, paddingTop: large ? 6 : 0 }}>
                {CAPITAL_PERIODS.map((r) => (
                    <button key={r} onClick={() => setPeriod(r)} style={{
                        flex: 1, padding: large ? '13px 0' : '6px 0', fontSize: large ? 15 : 11, fontWeight: 600,
                        borderRadius: large ? 10 : 8, cursor: 'pointer',
                        background: period === r ? 'rgba(255,255,255,0.08)' : 'transparent',
                        color: period === r ? 'var(--text)' : 'var(--text-mute)',
                        border: 'none',
                    }}>{r}</button>
                ))}
                {!large && fullscreenHref && (
                    <button
                        onClick={() => window.open(fullscreenHref, '_blank', 'noopener')}
                        title="Ouvrir en plein écran"
                        style={{
                            flexShrink: 0, width: 35, height: 35,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderRadius: 8, cursor: 'pointer',
                            background: 'transparent', color: 'var(--text-mute)', border: 'none',
                        }}
                    >
                        <ExpandIcon/>
                    </button>
                )}
            </div>
        </div>
    );
};

export default CapitalChart;
